import type { TFunction } from 'i18next';

/**
 * Words of a generation label (AUT-450 / VG-03): the restyling and market
 * names, and the auto-composed label of the ADD form.
 *
 * The stored label is the FULL display data («XV70 рестайлинг, Китай») —
 * there is no separate code column. On an add form, until the admin edits the
 * label by hand, changing «Рестайлинг» or «Рынок» rewrites it as
 * `<code><tail>` where the tail is ` рестайлинг` / ` 2-й рестайлинг` + `, Китай`
 * and the code is whatever precedes the current tail. An edit form never
 * rewrites the stored label.
 */
export const RESTYLING_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9] as const;
/** '' = sold broadly. Codes are lowercase (the API's `^[a-z]*$`). */
export const MARKET_OPTIONS = ['', 'cn'] as const;

const K = 'vehicleCatalog.generations';

/** «без рестайлинга» / «рестайлинг» / «2-й рестайлинг». */
export const restylingName = (t: TFunction, n: number): string => {
  if (n === 0) return t(`${K}.restyling.none`);
  if (n === 1) return t(`${K}.restyling.first`);
  return t(`${K}.restyling.nth`, { n });
};

/** «любой» / «Китай»; an unknown code reads as itself in capitals. */
export const marketName = (t: TFunction, code: string): string =>
  code === ''
    ? t(`${K}.market.any`)
    : t(`${K}.market.${code}`, { defaultValue: code.toUpperCase() });

/** The label's tail for a restyling + market — '' for an original, broad one. */
export const labelTail = (t: TFunction, restyling: number, market: string): string =>
  (restyling === 0 ? '' : ` ${restylingName(t, restyling)}`) +
  (market === '' ? '' : `, ${marketName(t, market)}`);

export const composeLabel = (code: string, tail: string): string =>
  code.trim() === '' ? tail.replace(/^[ ,]+/, '') : `${code.trimEnd()}${tail}`;

/** The code in front of `tail`, or `null` when the label no longer ends with it (hand-edited). */
export const codeOfLabel = (label: string, tail: string): string | null => {
  if (tail === '') return label;
  if (label === composeLabel('', tail)) return '';
  return label.endsWith(tail) ? label.slice(0, label.length - tail.length) : null;
};
